import { ChevronLeft, ChevronRight, UserPlus, X } from 'lucide-react'
import { useState } from 'react'

import { ActionInfoQuiet, Button, ErrorNotice, ModalFrame, QueryStatePanel, ScreenInfo, StatusMessage } from '@krp/ui-kit'

import { AddMembersDialog } from './AddMembersDialog'
import { Legend } from './Legend'
import { ScheduleGrid } from './ScheduleGrid'
import { ErrorBlock, SrOnly } from './parts'
import type { PlaceActions, PlaceVm } from './screenTypes'
import './schedule.css'

const SCREEN_INFO = [
  'График одного объекта за месяц: слева сотрудники объекта, сверху дни — число и день недели, справа «Всего» — сколько дней кассир выходит («Р»). Сегодняшний день в шапке синий. При нехватке места прокручиваются только дни.',
  'Левый клик по клетке ставит кассира на день («Р», клетка залита цветом кассира); ещё раз — очищает. Правый клик (на телефоне — долгое нажатие, с клавиатуры — клавиша меню или Shift+F10) открывает меню: «Р — Выходит», «В — Выходной день» (холодный тон со штриховкой), «Н — Не выходит» (серая клетка) и «Очистить». Серая клетка с приглушённой «Н» — день не отмечен. В «Всего» считаются только дни «Р». Сохранение идёт сразу, кнопки «Сохранить» нет; пока ответа нет, в клетке крутится загрузчик.',
  'Если кассир в этот день уже выходит («Р») на другом объекте, не отмеченная клетка выглядит как «Н»: при наведении в ней появляется замок, а в подсказке написано, на каком объекте он занят. Поставить «Р» сюда нельзя; «В» и «Н» ставятся всегда.',
  'Состав объекта хранится на сервере: добавленный кассир остаётся в таблице, пока вы сами его не уберёте. Кнопка «Добавить сотрудника» открывает окно со списком кассиров: отметьте нескольких и нажмите «Добавить».',
  'Значок «×» у строки убирает кассира с объекта после подтверждения: его выходы, начиная с сегодняшнего дня, снимаются, прошлые дни остаются в истории. Строка ушедшего кассира с прошлыми днями в показанном месяце остаётся без значка.',
  'Правка графика — у того, кому выдано право его править; остальные только смотрят.',
] as const

/** Заглушка на время загрузки: строки тех же размеров, чтобы экран не прыгал, когда придут данные. */
function GridSkeleton() {
  return (
    <div className="sch-skeleton" role="status" aria-live="polite">
      <SrOnly>Загружаем график…</SrOnly>
      {Array.from({ length: 4 }, (_, row) => (
        <div className="sch-skeleton__row" key={row} aria-hidden="true">
          {Array.from({ length: 7 }, (_, cell) => (
            <span className="sch-skeleton__cell" key={cell} />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Экран «Объект»: заголовок, навигация по месяцам, кнопка «Добавить сотрудника», легенда и таблица «сотрудники × дни месяца» —
 * каждая группа отдельной строкой сверху вниз. Левый клик по клетке ставит и снимает «выходит», правый открывает меню статусов (автосохранение, без кнопки);
 * кнопка «Добавить сотрудника» открывает окно выбора нескольких кассиров, значок у строки убирает. Экран ничего не запрашивает сам: vm и
 * действия — от binding; объект и месяц приходят из адреса окна. Название и архив объекта меняются в списке объектов.
 */
export function PlaceScreen({ vm, actions }: { vm: PlaceVm; actions: PlaceActions }) {
  const [adding, setAdding] = useState(false)
  const [leaving, setLeaving] = useState<{ cashierId: string; title: string } | null>(null)
  const rosterIds = new Set(vm.memberIds.map((id) => id.toLowerCase()))
  const loading = vm.load.status === 'loading' || vm.load.status === 'idle'

  return (
    <section aria-labelledby="schedule-place-title" className="sch">
      <header className="sch-head">
        <div className="sch-head__title">
          <h2 id="schedule-place-title">{vm.placeName ?? 'Объект графика'}</h2>
          <ScreenInfo title="График объекта" lines={SCREEN_INFO} />
        </div>
        {vm.archived && <p className="sch-lead">Объект в архиве: график можно смотреть, менять его нельзя.</p>}
      </header>

      <div className="sch-toolbar" role="toolbar" aria-label="Месяц графика">
        <div className="sch-toolbar__nav">
          <ActionInfoQuiet>
            <Button variant="secondary" icon={<ChevronLeft size={16} aria-hidden="true" />} aria-label="Предыдущий месяц" actionId="schedule.place.previous-month" onClick={actions.previous} />
            <strong className="sch-period" aria-live="polite">
              {vm.title}
            </strong>
            <Button variant="secondary" icon={<ChevronRight size={16} aria-hidden="true" />} aria-label="Следующий месяц" actionId="schedule.place.next-month" onClick={actions.next} />
            <Button variant="secondary" actionId="schedule.place.today" onClick={actions.today}>
              Текущий месяц
            </Button>
          </ActionInfoQuiet>
          {vm.refreshing && (
            <span className="sch-refreshing" role="status">
              Обновляется…
            </span>
          )}
        </div>
      </div>

      {vm.directoryError !== null && <ErrorNotice error={vm.directoryError} title="Имена кассиров не получены: кассиры подписаны по номеру" />}
      {vm.error !== null && <ErrorBlock error={vm.error} />}

      {loading ? (
        <GridSkeleton />
      ) : vm.load.status !== 'ready' ? (
        <QueryStatePanel
          query={vm.load}
          emptyText=""
          onRetry={actions.reload}
          retryActionId="schedule.place.reload"
          titles={{ forbidden: 'Нет доступа к графику выхода', unavailable: 'Служба графика недоступна', error: 'Не удалось получить график' }}
        />
      ) : vm.placeName === null ? (
        <StatusMessage kind="error" title="Объект не найден" text="Такого объекта нет: его могли убрать в архив или адрес неверен. Вернитесь к списку объектов." />
      ) : (
        <>
          {vm.editable && (
            <div className="sch-row">
              <Button variant="secondary" icon={<UserPlus size={16} aria-hidden="true" />} actionId="schedule.place.add-member" aria-haspopup="dialog" onClick={() => setAdding(true)}>
                Добавить сотрудника
              </Button>
            </div>
          )}
          <Legend />
          <ScheduleGrid
            caption={`Кассиры объекта по дням, ${vm.title}`}
            cornerLabel="Сотрудник"
            days={vm.days}
            rows={vm.rows}
            editable={vm.editable}
            refreshing={vm.refreshing}
            scrollKey={`${vm.placeName}|${vm.title}`}
            rowTools={
              vm.editable
                ? (row) =>
                    rosterIds.has(row.cashierId.toLowerCase()) ? (
                      <Button
                        variant="ghost"
                        icon={<X size={14} aria-hidden="true" />}
                        actionId="schedule.place.remove-member"
                        aria-label={`Убрать из состава: ${row.title}`}
                        onClick={() => setLeaving({ cashierId: row.cashierId, title: row.title })}
                      />
                    ) : null
                : undefined
            }
            denied={vm.denied}
            onToggle={actions.toggle}
            onSetKind={actions.setKind}
            onDismissDenied={actions.dismissDenied}
          />
          {adding && (
            <AddMembersDialog people={vm.people} memberIds={vm.memberIds} recent={vm.recent} onAdd={actions.addMember} onClose={() => setAdding(false)} />
          )}
          {leaving !== null && (
            <ModalFrame title="Убрать кассира с объекта" size="sm" covered={false} onClose={() => setLeaving(null)}>
              <div className="sch-editor__confirm" role="group" aria-label="Убрать кассира с объекта">
                <p>
                  Убрать кассира «{leaving.title}» с объекта «{vm.placeName}»? Его выходы, начиная с сегодняшнего дня, будут сняты; прошлые дни сохранятся в истории.
                </p>
                <div className="sch-editor__buttons">
                  <Button
                    variant="danger"
                    actionId="schedule.place.remove-member-confirm"
                    onClick={() => {
                      setLeaving(null)
                      actions.removeMember(leaving.cashierId)
                    }}
                  >
                    Убрать
                  </Button>
                  <Button variant="ghost" actionId="schedule.place.remove-member-cancel" onClick={() => setLeaving(null)}>
                    Не убирать
                  </Button>
                </div>
              </div>
            </ModalFrame>
          )}
          {vm.rows.length === 0 && (
            <StatusMessage
              kind="empty"
              title="В составе объекта пока никого нет"
              text={vm.editable ? 'Нажмите «Добавить сотрудника» и отметьте кассиров — они появятся строками и останутся в составе.' : 'Кассиров в состав объекта добавляет тот, кому выдано право править график.'}
            />
          )}
        </>
      )}
    </section>
  )
}
